import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import "./PriceInfo.scss";
import PriceInfoIcon from "../icons/PriceInfoIcon";
import {currencyConverter} from "../../helper/currency";

const POPUP_WIDTH = 300; // keep in sync with width in PriceInfo.scss
const POPUP_EDGE_PADDING = 20;

/**
 * PriceInfo component showing a popup with price details.
 * It closes when clicking outside the popup.
 *
 * Positioning strategy: the popup uses `position: fixed` and viewport-relative
 * coordinates derived from the trigger button's getBoundingClientRect(). This
 * keeps the popup correctly anchored to the trigger regardless of how the app
 * is embedded (standalone or as a fragment inside a host page) — the surrounding
 * DOM/CSS context (offset parents, centered wrappers, etc.) no longer affects it.
 * Position is recomputed on scroll/resize while the popup is open.
 */
const PriceInfo = ({
                       t,
                       price,
                       recommendedRetailPrice,
                       historicMinimalPurchasePrice,
                       discountRate,
                       showInfo,
                       setShowInfo,
                       showMsrp,
                       className,
                   }) => {
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const [coords, setCoords] = useState({top: 0, left: 0, alignRight: true});

    const computeCoords = useCallback(() => {
        if (!triggerRef.current) return null;
        const rect = triggerRef.current.getBoundingClientRect();
        const canFitLeft = rect.left > POPUP_WIDTH - POPUP_EDGE_PADDING;
        return {
            // viewport-relative — used together with `position: fixed`
            top: rect.bottom,
            left: rect.left + (rect.width / 2),
            alignRight: canFitLeft,
        };
    }, []);

    const updatePosition = useCallback(() => {
        const next = computeCoords();
        if (next) setCoords(next);
    }, [computeCoords]);

    // Keep the popup anchored to the trigger while it's open: recompute on
    // scroll (capture phase to catch nested scroll containers) and on resize.
    useLayoutEffect(() => {
        if (!showInfo) return undefined;
        updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [showInfo, updatePosition]);

    useEffect(() => {
        function handleClickOutside(event) {
            const popup = document.getElementById("price-info-popup-root");
            if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
                popup && !popup.contains(event.target)) {
                setShowInfo(false);
            }
        }

        if (showInfo) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showInfo, setShowInfo]);

    const toggleInfo = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Pre-compute on click so the popup paints in the right place on the first frame
        if (!showInfo) {
            const next = computeCoords();
            if (next) setCoords(next);
        }
        setShowInfo(!showInfo);
    };

    const popupContent = (
        <div
            id="price-info-popup-root"
            className={`price-info-popup ${coords.alignRight ? "align-right" : "align-left"} ${className || ""}`}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
            }}
            style={{
                position: "fixed",
                top: `${coords.top}px`,
                left: `${coords.left}px`
            }}
        >
            <div className="price-info-header">
                <h3>{t("productDetail.priceDetailsHeadline")}</h3>
                <button
                    type="button"
                    className="close-button"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowInfo(false);
                    }}
                    aria-label={t("feedback.close")}
                >
                    &times;
                </button>
            </div>
            <div className="price-info-content">
                <div className="price-item">
                    <div className="item-label">{t("productDetail.priceToday")}:</div>
                    <div className="item-value current-price">
                        {price ? currencyConverter(price) : t("productDetail.priceOnRequest")}
                        <span className="vat-info"> {t("productDetail.inclVAT")}</span>
                    </div>
                </div>

                {historicMinimalPurchasePrice && (
                    <div className="price-item">
                        <div className="item-label">{t("productDetail.priceDetails30Days")}:</div>
                        <div className="item-value historic-price">
                            {currencyConverter(historicMinimalPurchasePrice)}
                            {discountRate > 0 && <span className="discount-value"> -{discountRate}%</span>}
                        </div>
                    </div>
                )}

                {(showMsrp && recommendedRetailPrice) && (
                    <div className="price-item">
                        <div className="item-label">{t("productDetail.recommendedRetailPrice")}:</div>
                        <div className="item-value original-price">
                            {currencyConverter(recommendedRetailPrice)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const portalRoot = document.getElementById("hpm-portalRoot");

    return (
        <div className="price-info-container" ref={wrapperRef}>
            <button
                type="button"
                ref={triggerRef}
                aria-label={t("productDetail.priceDetails")}
                className="price-info-trigger"
                onClick={toggleInfo}
            >
                <PriceInfoIcon/>
            </button>

            {showInfo && portalRoot && ReactDOM.createPortal(popupContent, portalRoot)}
        </div>
    );
};

export default PriceInfo;
